import { SelectOption } from '@navstation-portal/shared/data-model/common';
import { getIsoGradeOptions } from '../utils/fuel-util';

export type FuelOptionLike = {
  name: string;
  code: unknown;
  disabled?: boolean;
};

export interface FuelSpecSelectionEntryLike {
  fuelType?: { value?: FuelOptionLike | undefined };
  isoFuelGrade?: { value?: FuelOptionLike | undefined };
}

export function toFuelOptionKey(value: unknown): string | undefined {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (value && typeof value === 'object' && '$oid' in value) {
    const oid = (value as { $oid?: unknown }).$oid;
    return typeof oid === 'string' ? oid : undefined;
  }

  return undefined;
}

function getFuelTypeCode(value?: SelectOption): string | undefined {
  return (value as SelectOption | undefined)?.code as string | undefined;
}

function buildIsoSelectionsByFuelType(entries: FuelSpecSelectionEntryLike[]): Map<string, Set<string>> {
  const isoSelectionsByFuelType = new Map<string, Set<string>>();

  entries.forEach((entry) => {
    const fuelTypeCode = getFuelTypeCode(entry.fuelType?.value);
    const isoCode = toFuelOptionKey(entry.isoFuelGrade?.value?.code);
    if (!fuelTypeCode || !isoCode) return;

    const currentIsoSelections = isoSelectionsByFuelType.get(fuelTypeCode);
    if (currentIsoSelections) {
      currentIsoSelections.add(isoCode);
      return;
    }

    isoSelectionsByFuelType.set(fuelTypeCode, new Set([isoCode]));
  });

  return isoSelectionsByFuelType;
}

function withDisabledState<T extends FuelOptionLike>(option: T, disabled: boolean): T {
  if (!disabled || option.disabled) {
    return option;
  }

  return {
    ...option,
    disabled: true,
  };
}

export function buildFuelTypeOptionsByRow<T extends FuelOptionLike>(
  entries: FuelSpecSelectionEntryLike[],
  availableFuelTypeOptions: T[]
): T[][] {
  const isoSelectionsByFuelType = buildIsoSelectionsByFuelType(entries);

  return entries.map((entry) => {
    const currentSelection = entry.fuelType?.value;
    const currentSelectionCode = getFuelTypeCode(currentSelection);
    const currentSelectionName = currentSelection?.name;

    return availableFuelTypeOptions.map((option) => {
      const optionCode = getFuelTypeCode(option);
      if (!optionCode) return option;

      const selectedIsoCount = isoSelectionsByFuelType.get(optionCode)?.size ?? 0;
      const isoOptionCount = Math.max(getIsoGradeOptions(option).length, 1);
      const allIsoOptionsSelected = selectedIsoCount >= isoOptionCount;
      const isCurrentSelection =
        (currentSelectionCode && optionCode === currentSelectionCode) ||
        (!!currentSelectionName && option.name === currentSelectionName);

      return withDisabledState(option, !isCurrentSelection && allIsoOptionsSelected);
    });
  });
}

export function getIsoGradeOptionsByRow(entries: FuelSpecSelectionEntryLike[], entryIndex: number): FuelOptionLike[] {
  const entry = entries[entryIndex];
  const fuelType = entry?.fuelType?.value;
  if (!fuelType) return [];

  const fuelTypeCode = getFuelTypeCode(fuelType);
  if (!fuelTypeCode) return [];

  const currentIsoCode = toFuelOptionKey(entry?.isoFuelGrade?.value?.code);
  const selectedIsoCodes = new Set(
    entries
      .filter((candidate, index) => {
        if (index === entryIndex) return false;
        return getFuelTypeCode(candidate.fuelType?.value) === fuelTypeCode;
      })
      .map((candidate) => toFuelOptionKey(candidate.isoFuelGrade?.value?.code))
      .filter((code): code is string => typeof code === 'string')
  );

  return getIsoGradeOptions(fuelType).map((option: SelectOption) => {
    const optionCode = toFuelOptionKey(option.code);

    return withDisabledState(option, !!optionCode && optionCode !== currentIsoCode && selectedIsoCodes.has(optionCode));
  });
}
